import { data as routerData } from 'react-router';
import { z } from 'zod';
import type { LoaderFunctionArgs } from '~/types/route-args';
import type {
    GetEnquiryFormForPageQuery,
    GetEnquiryFormForPageQueryVariables,
    GetEnquiryFormForTeamMemberQuery,
    GetEnquiryFormForTeamMemberQueryVariables,
} from 'generated/graphql';
import { EnquiryFormPageType, EnquiryFormTrigger } from 'generated/graphql';
import { GetEnquiryFormForPageDocument } from 'generated/documents/GetEnquiryFormForPageDocument';
import { GetEnquiryFormForTeamMemberDocument } from 'generated/documents/GetEnquiryFormForTeamMemberDocument';
import type { EnquiryFormConfig } from '~/modules/enquiry/types/public-enquiry-form';
import { toPublicEnquiryForm } from '~/modules/enquiry/domain/public-enquiry-form-mapper';
import { BM_FORMS_FEATURE } from '~/modules/business-manager/forms/api/load-forms.server';
import { hasFeature } from '~/modules/root/hook/use-feature';

export const ENQUIRY_FORM_API_ROUTE = '/remix-api/enquiry-form';

const QUERY_SCHEMA = z.union([
    z.object({
        professionalId: z.coerce.number().int().positive(),
        pageType: z.nativeEnum(EnquiryFormPageType),
        trigger: z.nativeEnum(EnquiryFormTrigger),
        // Product pages: the product shown, whose own button choice wins.
        productId: z.coerce.number().int().positive().optional(),
    }),
    // A team member's own booking form. `off` means they have none, and the
    // Book button books them without questions.
    z.object({
        teamMemberId: z.coerce.number().int().positive(),
    }),
]);

/**
 * `legacy` — Forms is not on for this site, keep the flat professional
 * settings. `off` — the professional switched this CTA off. `form` — use the
 * returned configuration.
 */
export interface EnquiryFormApiResponse extends EnquiryFormConfig {
    error?: string;
}

/**
 * Resolves which enquiry form a public page's CTA should open. Public pages
 * call this lazily so their loaders stay untouched. The response carries each
 * routing rule's conditions, destination kind, meeting session and decline
 * message (so the form can say a meeting will be offered or show the message
 * up front), but never recipients: rule emails, teammates, default teammates,
 * fallback emails and CRM hints are blanked by the API for anyone who cannot
 * manage the form.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const parsed = QUERY_SCHEMA.safeParse(Object.fromEntries(url.searchParams));
    if (!parsed.success) {
        return routerData<EnquiryFormApiResponse>(
            { status: 'legacy', form: null, error: 'Invalid query parameters' },
            { status: 400 },
        );
    }

    if (!hasFeature(context.featureFlags, BM_FORMS_FEATURE)) {
        return routerData<EnquiryFormApiResponse>(
            { status: 'legacy', form: null },
            { headers: { 'Cache-Control': 'private, max-age=60' } },
        );
    }

    try {
        const query = parsed.data;
        const form =
            'teamMemberId' in query
                ? (
                      await context.client.query<
                          GetEnquiryFormForTeamMemberQuery,
                          GetEnquiryFormForTeamMemberQueryVariables
                      >({
                          query: GetEnquiryFormForTeamMemberDocument,
                          variables: { teamMemberID: query.teamMemberId },
                      })
                  ).data?.enquiryFormForTeamMember
                : (
                      await context.client.query<
                          GetEnquiryFormForPageQuery,
                          GetEnquiryFormForPageQueryVariables
                      >({
                          query: GetEnquiryFormForPageDocument,
                          variables: {
                              professionalID: query.professionalId,
                              pageType: query.pageType,
                              trigger: query.trigger,
                              productID: query.productId ?? null,
                          },
                      })
                  ).data?.enquiryFormForPage;

        return routerData<EnquiryFormApiResponse>(
            form
                ? { status: 'form', form: toPublicEnquiryForm(form) }
                : { status: 'off', form: null },
            { headers: { 'Cache-Control': 'private, max-age=60' } },
        );
    } catch (error) {
        context.logger.error(`Error in ${ENQUIRY_FORM_API_ROUTE}`, { error });
        // Fail open to the legacy behaviour rather than blocking enquiries.
        return routerData<EnquiryFormApiResponse>(
            { status: 'legacy', form: null, error: 'Unable to resolve form' },
            { status: 500 },
        );
    }
};
